<script lang="ts">
  import { onMount } from 'svelte'
  import { tStore } from '../../i18n'
  import { sourcesApi } from '../../api/sources'
  import type { CreateSourceRequest, Source } from '../../api/sources'
  import { ApiError, isApiError } from '../../api/errors'
  import { useToast } from '../../composables/useToast'
  import { useAbortable } from '../../composables/useAbortable'
  import { formatDateTime } from '../../composables/format'
  import { retrievalTriggerErrorMessage } from '../../composables/retrievalTriggerError'
  import Dropdown from '../../components/Dropdown.svelte'

  const scheduleHoursOptions = [
    { value: 24, label: '24h' },
    { value: 48, label: '48h' },
  ]

  const toast = useToast()
  const { start } = useAbortable()

  let sources = $state<Source[]>([])
  let loading = $state(true)
  let loadError = $state<ApiError | null>(null)

  // Per-row in-flight indicators so buttons can show a spinner without
  // disabling the whole table.
  let pendingTrigger = $state<Set<number>>(new Set())
  let pendingToggle = $state<Set<number>>(new Set())

  // Create form
  let showForm = $state(false)
  let creating = $state(false)
  let form = $state<{
    name: string
    displayName: string
    url: string
    scheduleHours: number | null
  }>({
    name: '',
    displayName: '',
    url: '',
    scheduleHours: 24,
  })
  let formError = $state<string | null>(null)

  onMount(() => {
    void loadSources()
  })

  async function loadSources() {
    const signal = start()
    loading = true
    loadError = null
    try {
      sources = await sourcesApi.list({ signal })
    } catch (err) {
      if (signal.aborted) return // reloaded or navigated away mid-request
      loadError = toApiError(err)
    } finally {
      if (!signal.aborted) loading = false
    }
  }

  // The crawl runs asynchronously: the trigger returns 202 before the spider
  // opens and writes its retrieval_log row, so "last run" updates a beat later.
  // Poll a few times after a trigger until this source's lastRunAt changes.
  const RUN_POLL_ATTEMPTS = 6
  const RUN_POLL_INTERVAL_MS = 1000

  function delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  async function refreshUntilNewRun(sourceId: number, previousLastRun: string | null) {
    for (let attempt = 0; attempt < RUN_POLL_ATTEMPTS; attempt++) {
      await delay(RUN_POLL_INTERVAL_MS)
      let latest: Source[]
      try {
        latest = await sourcesApi.list()
      } catch {
        continue // transient read failure — keep polling
      }
      sources = latest // quiet refresh, no full-table loading spinner
      const updated = latest.find((s) => s.id === sourceId)
      if (updated && updated.lastRunAt !== previousLastRun) return
    }
  }

  async function trigger(source: Source) {
    pendingTrigger = new Set(pendingTrigger).add(source.id)
    const previousLastRun = source.lastRunAt
    try {
      await sourcesApi.triggerRetrieval(source.id)
      toast.show($tStore('common.collectionStarted', { name: source.displayName }), 'success')
      await refreshUntilNewRun(source.id, previousLastRun)
    } catch (err) {
      toast.show(retrievalTriggerErrorMessage(err), 'error')
    } finally {
      const next = new Set(pendingTrigger)
      next.delete(source.id)
      pendingTrigger = next
    }
  }

  async function toggleEnabled(source: Source) {
    pendingToggle = new Set(pendingToggle).add(source.id)
    try {
      const updated = await sourcesApi.update(source.id, {
        enabled: !source.enabled,
      })
      sources = sources.map((s) => (s.id === updated.id ? updated : s))
    } catch (err) {
      const apiErr = toApiError(err)
      toast.show(apiErr.message, 'error')
    } finally {
      const next = new Set(pendingToggle)
      next.delete(source.id)
      pendingToggle = next
    }
  }

  async function submit(event: Event) {
    event.preventDefault()
    formError = null
    creating = true
    try {
      const payload: CreateSourceRequest = {
        name: form.name.trim().toLowerCase(),
        displayName: form.displayName.trim(),
        url: form.url.trim(),
        scheduleHours: form.scheduleHours,
      }
      const created = await sourcesApi.create(payload)
      sources = [...sources, created].sort((a, b) => a.name.localeCompare(b.name))
      resetForm()
      showForm = false
      toast.show($tStore('views.sources.createSuccess'), 'success')
    } catch (err) {
      const apiErr = toApiError(err)
      formError = apiErr.message
    } finally {
      creating = false
    }
  }

  function resetForm() {
    form = {
      name: '',
      displayName: '',
      url: '',
      scheduleHours: 24,
    }
    formError = null
  }

  function toApiError(err: unknown): ApiError {
    if (isApiError(err)) return err
    return new ApiError({
      status: 0,
      code: 'UNKNOWN',
      message: err instanceof Error ? err.message : String(err),
    })
  }
</script>

<section class="sources">
  <header class="sources__header">
    <h2>{$tStore('views.sources.title')}</h2>
    <div class="sources__actions">
      <button class="btn btn--secondary" onclick={loadSources} disabled={loading}>
        {$tStore('views.sources.refresh')}
      </button>
      <button class="btn btn--primary" onclick={() => (showForm = !showForm)}>
        {showForm ? $tStore('views.sources.cancel') : $tStore('views.sources.add')}
      </button>
    </div>
  </header>

  {#if showForm}
    <form class="card form" onsubmit={submit}>
      <h3>{$tStore('views.sources.addTitle')}</h3>

      <div class="form__row">
        <label>
          <span>{$tStore('views.sources.fields.name')}</span>
          <input type="text" bind:value={form.displayName} required placeholder="Narodne novine" />
        </label>
        <label>
          <span>{$tStore('views.sources.fields.identifier')}</span>
          <input
            type="text"
            class="mono"
            bind:value={form.name}
            required
            placeholder="narodne-novine"
          />
        </label>
      </div>

      <div class="form__row">
        <label>
          <span>{$tStore('views.sources.fields.url')}</span>
          <input
            type="url"
            bind:value={form.url}
            required
            placeholder="https://narodne-novine.nn.hr/"
          />
        </label>
        <label>
          <span>{$tStore('views.sources.fields.scheduleHours')}</span>
          <Dropdown bind:value={form.scheduleHours} options={scheduleHoursOptions} />
        </label>
      </div>

      {#if formError}
        <p class="form__error">{formError}</p>
      {/if}

      <div class="form__actions">
        <button type="button" class="btn btn--secondary" onclick={() => (showForm = false)}>
          {$tStore('views.sources.cancel')}
        </button>
        <button type="submit" class="btn btn--primary" disabled={creating}>
          {creating ? $tStore('views.sources.saving') : $tStore('views.sources.save')}
        </button>
      </div>
    </form>
  {/if}

  {#if loading}
    <p class="muted">{$tStore('views.sources.loading')}</p>
  {:else if loadError}
    <p class="error">{loadError.message}</p>
  {:else if sources.length === 0}
    <p class="muted">{$tStore('views.sources.empty')}</p>
  {:else}
    <div class="card table-card">
      <table class="table">
        <colgroup>
          <col class="col-name" />
          <col class="col-identifier" />
          <col class="col-url" />
          <col class="col-status" />
          <col class="col-schedule" />
          <col class="col-last-run" />
          <col class="col-actions" />
        </colgroup>
        <thead>
          <tr>
            <th>{$tStore('views.sources.fields.name')}</th>
            <th>{$tStore('views.sources.fields.identifier')}</th>
            <th>{$tStore('views.sources.fields.url')}</th>
            <th class="table__status-col">{$tStore('views.sources.fields.status')}</th>
            <th class="table__schedule-col">{$tStore('views.sources.fields.scheduleHours')}</th>
            <th>{$tStore('views.sources.fields.lastRunAt')}</th>
            <th class="table__actions-col">{$tStore('views.sources.fields.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {#each sources as source (source.id)}
            <tr>
              <td class="source-name">{source.displayName}</td>
              <td class="mono source-identifier">{source.name}</td>
              <td class="url">
                <a href={source.url} target="_blank" rel="noopener noreferrer">{source.url}</a>
              </td>
              <td class="table__status-col">
                <span
                  class="badge"
                  class:badge--on={source.enabled}
                  class:badge--off={!source.enabled}
                >
                  {source.enabled
                    ? $tStore('views.sources.status.enabled')
                    : $tStore('views.sources.status.disabled')}
                </span>
              </td>
              <td class="table__schedule-col">{source.scheduleHours ?? '—'}</td>
              <td>{formatDateTime(source.lastRunAt)}</td>
              <td class="table__actions-col">
                <div class="table__actions">
                  <button
                    class="btn btn--sm btn--primary"
                    onclick={() => trigger(source)}
                    disabled={pendingTrigger.has(source.id) || !source.enabled}
                    title={!source.enabled ? $tStore('views.sources.triggerDisabledHint') : ''}
                  >
                    {pendingTrigger.has(source.id) ? '…' : $tStore('views.sources.trigger')}
                  </button>
                  <button
                    class="btn btn--sm btn--secondary"
                    onclick={() => toggleEnabled(source)}
                    disabled={pendingToggle.has(source.id)}
                  >
                    {source.enabled
                      ? $tStore('views.sources.disable')
                      : $tStore('views.sources.enable')}
                  </button>
                </div>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</section>

<style lang="scss">
  @use '../../assets/styles/variables' as v;

  .sources {
    display: flex;
    flex-direction: column;
    gap: v.$space-lg;
  }

  .sources__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: v.$space-md;
    flex-wrap: wrap;
  }

  .sources__actions {
    display: flex;
    gap: v.$space-sm;
  }

  .card {
    background: v.$color-surface-elevated;
    border: 1px solid v.$color-border;
    border-radius: v.$radius-md;
    padding: v.$space-lg;
  }

  .form {
    display: flex;
    flex-direction: column;
    gap: v.$space-md;

    h3 {
      margin: 0;
    }
  }

  .form__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: v.$space-md;

    label {
      display: flex;
      flex-direction: column;
      gap: v.$space-xs;
      font-size: 0.875rem;

      span {
        color: v.$color-text-muted;
      }

      input {
        padding: v.$space-sm v.$space-md;
        border: 1px solid v.$color-border;
        border-radius: v.$radius-sm;
        font-family: inherit;
        font-size: 1rem;

        &.mono {
          font-family: v.$font-mono;
        }
      }
    }

    @media (max-width: v.$bp-md) {
      grid-template-columns: 1fr;
    }
  }

  .form__error {
    color: v.$color-danger-fg;
    background: v.$color-danger-bg;
    padding: v.$space-sm v.$space-md;
    border-radius: v.$radius-sm;
    margin: 0;
  }

  .form__actions {
    display: flex;
    justify-content: flex-end;
    gap: v.$space-sm;
  }

  .table-card {
    // Below the table's min-width (see .table), the table no longer fits
    // the card — scroll the card horizontally instead of squeezing columns
    // down to unreadable widths or letting content spill out uncontained.
    overflow-x: auto;
  }

  .table {
    width: 100%;
    // The floor below which columns stop being readable (long enough to
    // fit both action buttons on one line, a status badge, a source name
    // like "oeffentlichevergabe" without shattering it, etc). Above this
    // the table fills the card exactly; below it, table-layout:fixed would
    // otherwise keep shrinking columns toward zero instead of triggering
    // the card's scrollbar.
    min-width: 74rem;
    table-layout: fixed;
    border-collapse: collapse;
    font-size: 0.9375rem;

    th,
    td {
      text-align: left;
      padding: v.$space-sm v.$space-md;
      border-bottom: 1px solid v.$color-border;
      vertical-align: middle;
      overflow-wrap: break-word;
    }

    th {
      font-weight: 600;
      color: v.$color-text-muted;
      background: v.$color-surface;
    }

    tbody tr:last-child td {
      border-bottom: none;
    }

    .mono {
      font-family: v.$font-mono;
    }

    .source-name {
      font-weight: 600;
    }

    .source-identifier {
      color: v.$color-text-muted;
      font-size: 0.875rem;
    }

    .url a {
      color: v.$color-primary;
      text-decoration: none;
      display: inline-block;
      max-width: 100%;
      word-break: break-word;
      overflow-wrap: anywhere;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  // Percentages (not a mix of % and fixed units) so `table-layout: fixed`
  // always resolves the columns to exactly 100% of the card's width,
  // whatever that width turns out to be.
  .col-name {
    // The source's human-readable display name — the primary, most
    // prominent column. Wide enough for the longest single unbreakable
    // token among current sources (e.g. "öffentlichevergabe.de") to stay
    // on one line instead of stranding a stray character.
    width: 18%;
  }

  .col-identifier {
    // The source's internal identifier (mono, no spaces — often
    // underscore_joined), shown as secondary/technical. Wide enough that
    // it wraps at worst into two balanced halves instead of stranding a
    // couple of stray characters on their own line.
    width: 18%;
  }

  .col-url {
    // URLs are the longest content overall, but they wrap gracefully at
    // slashes, so unlike name/display-name they don't need a wide floor
    // to avoid an ugly break — this stays bounded so it can't push the
    // other columns off the edge of the card.
    width: 18%;
  }

  .col-status {
    width: 11%;
  }

  .col-schedule {
    width: 9%;
  }

  .col-last-run {
    width: 11%;
  }

  .col-actions {
    // Wide enough for both buttons on one line at the table's min-width;
    // the card's overflow-x scroll covers any viewport narrower than that.
    width: 15%;
  }

  .table__actions-col {
    white-space: nowrap;
  }

  .table__actions {
    // Left-aligned and single-line so it stays flush under the "Actions"
    // header and in line with every other (left-aligned) column, instead
    // of floating right with a gap before it or wrapping onto two lines.
    display: flex;
    flex-wrap: nowrap;
    gap: v.$space-xs;
    justify-content: flex-start;
  }

  .badge {
    // `table-layout: fixed` can still squeeze this column below the
    // badge's natural width on a narrow viewport; wrap/break instead of
    // silently overflowing into the next column.
    display: inline-block;
    max-width: 100%;
    overflow-wrap: break-word;
    padding: 0.125rem 0.5rem;
    border-radius: v.$radius-sm;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-right: v.$space-xs;

    &--on {
      background: v.$color-success-bg;
      color: v.$color-success-fg;
    }

    &--off {
      background: v.$color-danger-bg;
      color: v.$color-danger-fg;
    }
  }

  .btn {
    padding: v.$space-sm v.$space-md;
    border-radius: v.$radius-sm;
    font-family: inherit;
    font-size: 0.9375rem;
    border: 1px solid transparent;
    cursor: pointer;
    transition:
      background-color 120ms ease,
      border-color 120ms ease;

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }

    &--primary {
      background: v.$color-primary;
      color: v.$color-on-primary;

      &:hover:not(:disabled) {
        background: v.$color-primary-hover;
      }
    }

    &--secondary {
      background: v.$color-surface-elevated;
      color: v.$color-text;
      border-color: v.$color-border;

      &:hover:not(:disabled) {
        background: v.$color-surface;
      }
    }

    &--sm {
      padding: v.$space-xs v.$space-sm;
      font-size: 0.8125rem;
    }
  }

  .muted {
    color: v.$color-text-muted;
  }

  .error {
    color: v.$color-danger-fg;
    background: v.$color-danger-bg;
    padding: v.$space-sm v.$space-md;
    border-radius: v.$radius-sm;
  }
</style>
